import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Divider,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    FormGroup,
    Alert,
    Chip
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

const DuplicateResolutionModal = ({
    open,
    onClose,
    duplicateData,
    onResolved
}) => {
    const [resolution, setResolution] = React.useState('skip'); // 'skip', 'merge', 'create'
    const [selectedFields, setSelectedFields] = React.useState([]);

    const existing = duplicateData?.competition;
    const aiComp = duplicateData?.ai_competition?.competition_details;

    React.useEffect(() => {
        if (open) {
            setResolution('skip');
            setSelectedFields([]);
        }
    }, [open]);

    const handleFieldToggle = (field) => {
        if (selectedFields.includes(field)) {
            setSelectedFields(selectedFields.filter(f => f !== field));
        } else {
            setSelectedFields([...selectedFields, field]);
        }
    };

    const handleConfirm = () => {
        onResolved({
            action: resolution,
            selected_fields: resolution === 'merge' ? selectedFields : [],
            existing_id: existing?.id,
            ai_competition: duplicateData?.ai_competition
        });
        onClose();
    };

    const fieldsToCompare = [
        { key: 'memo', label: 'Description/Memo', existing: existing?.memo, ai: aiComp?.memo },
        { key: 'subject', label: 'Subject Tags', existing: existing?.subject?.join(', '), ai: aiComp?.subject?.join(', ') },
        { key: 'eligibility_memo', label: 'Eligibility', existing: existing?.eligibility_memo, ai: aiComp?.eligibility_memo },
        { key: 'test_format_memo', label: 'Test Format', existing: existing?.test_format_memo, ai: aiComp?.test_format_memo },
        { key: 'url', label: 'URL', existing: existing?.url, ai: aiComp?.url }
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CompareArrowsIcon />
                    Duplicate Competition Detected
                </Box>
            </DialogTitle>

            <DialogContent>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    A similar competition already exists in the database.
                    {duplicateData && (
                        <>
                            {' '}Match type: <strong>{duplicateData.match_type}</strong>
                            {' '}({Math.round((duplicateData.similarity_score || 0) * 100)}% similarity)
                        </>
                    )}
                </Alert>

                {/* Resolution Options */}
                <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        How would you like to proceed?
                    </Typography>
                    <RadioGroup
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                    >
                        <FormControlLabel
                            value="skip"
                            control={<Radio />}
                            label="Skip - Keep existing competition only"
                        />
                        <FormControlLabel
                            value="merge"
                            control={<Radio />}
                            label="Merge - Update existing competition with AI data"
                        />
                        <FormControlLabel
                            value="create"
                            control={<Radio />}
                            label="Create New - Save as separate competition"
                        />
                    </RadioGroup>
                </FormControl>

                {/* Field Comparison (shown for merge option) */}
                {resolution === 'merge' && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Select fields to update:
                        </Typography>
                        <FormGroup>
                            {fieldsToCompare.map((field) => (
                                <Box key={field.key} sx={{ mb: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedFields.includes(field.key)}
                                                onChange={() => handleFieldToggle(field.key)}
                                            />
                                        }
                                        label={field.label}
                                    />
                                    <Box sx={{ ml: 4, mt: 1 }}>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Current (Database)
                                                </Typography>
                                                <Typography variant="body2">
                                                    {field.existing || <em>Not set</em>}
                                                </Typography>
                                            </Box>
                                            <Divider orientation="vertical" flexItem />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="caption" color="primary">
                                                    AI Recommendation
                                                </Typography>
                                                <Typography variant="body2" color="primary">
                                                    {field.ai || <em>Not set</em>}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </FormGroup>
                    </Box>
                )}

                {/* Side-by-Side Comparison */}
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                    Comparison
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Existing Competition */}
                    <Box sx={{ flex: 1, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Existing Competition
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            {existing?.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                            <Chip
                                label={existing?.competition_type === 'test' ? 'Test' : 'Competition'}
                                size="small"
                                color="default"
                            />
                            {existing?.subject?.map((s, i) => (
                                <Chip key={i} label={s} size="small" variant="outlined" />
                            ))}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {existing?.memo?.substring(0, 150)}
                            {existing?.memo?.length > 150 ? '...' : ''}
                        </Typography>
                    </Box>

                    {/* AI Recommendation */}
                    <Box sx={{ flex: 1, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom color="primary">
                            AI Recommendation
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            {aiComp?.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                            <Chip
                                label={aiComp?.competition_type === 'test' ? 'Test' : 'Competition'}
                                size="small"
                                color="primary"
                            />
                            {aiComp?.subject?.map((s, i) => (
                                <Chip key={i} label={s} size="small" variant="outlined" />
                            ))}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {aiComp?.memo?.substring(0, 150)}
                            {aiComp?.memo?.length > 150 ? '...' : ''}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={resolution === 'merge' && selectedFields.length === 0}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DuplicateResolutionModal;
